import { useEditor } from "@tiptap/react";
import { useEffect, useRef } from "react";
import type { AnyExtension } from "@tiptap/core";

import { ExtensionKit } from "../extensions/extension-kit";
import { resolvePastedContent } from "../utils/pasteHtml";

// https://tiptap.dev/docs/guides/performance
export const useBlockEditor = ({ onChange, onBlur }: { onChange: (json: any) => void; onBlur: () => void }) => {
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onBlurRef.current = onBlur; }, [onBlur]);

  const extensions = [...ExtensionKit].filter((e): e is AnyExtension => e !== undefined);

  const editor = useEditor(
    {
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      autofocus: true,
      onCreate: (ctx) => {
        ctx.editor.commands.focus("start", { scrollIntoView: true });
      },
      /**
       * Triggered on every change
       *
       * Update local state
       */
      onUpdate: ({ editor }) => {
        const json = editor.getJSON();
        onChangeRef.current(json);
      },
      /** Trigger API save */
      onBlur() {
        onBlurRef.current();
      },
      extensions,
      editorProps: {
        attributes: {
          autocomplete: "off",
          autocorrect: "off",
          autocapitalize: "off",
          class: "",
        },
        // Pasting from external sources (browser pages, Word, etc.) is routed
        // through our own schema instead of the browser's default clipboard
        // parsing, so only nodes/marks the editor actually supports can ever
        // land — e.g. text color survives, but foreign font-family,
        // background-color, and non-heading font sizes never do.
        //
        // Content copied via our own "Copy to clipboard" node action (see
        // ContentItemMenu/useContentItemActions) is exempted from that
        // restriction, checked in priority order:
        //  1. INTERNAL_NODE_CLIPBOARD_MIME — the node's raw JSON, stashed
        //     alongside the browser's own clipboard data by that same action.
        //     Works for every node type with perfect fidelity, image included
        //     — it never touches HTML/DOM parsing, which matters because
        //     ImageBlock.parseHTML() unconditionally refuses to match <img>
        //     ("prevent importing images"), so an image can't be reconstructed
        //     from HTML at all, by any means.
        //  2. data-pm-slice — ProseMirror's own clipboard marker (set on any
        //     native Ctrl+C/Ctrl+X too, not just our button). Falling through
        //     to it lets ProseMirror's native reconstruction run instead of
        //     the restricted vocabulary below, for whichever node types *can*
        //     round-trip through HTML (table/columns/signature/mention).
        // Routing internally-copied content through the external-paste
        // vocabulary below instead silently dropped node types it doesn't
        // list (not in PASTE_ALLOWED_NODES, no text to unwrap into either),
        // which is why copying a node from one custom section and pasting
        // into another appeared to do nothing.
        handlePaste: (_view, event) => {
          const resolved = resolvePastedContent(event.clipboardData, extensions);
          if (!resolved || resolved.kind === "deferToNative") return false;

          event.preventDefault();
          editor.chain().focus().insertContent(resolved.content as any).run();
          return true;
        },
      },
      onContentError({ editor, error }) {
        // Your error handling logic here
        console.error(error);
      },
      // https://tiptap.dev/docs/guides/invalid-schema
      enableContentCheck: true,
      // This option gives us the control to enable the default behavior of rendering the editor immediately.
      // immediatelyRender: true,
      // This option gives us the control to disable the default behavior of re-rendering the editor on every transaction.
      // shouldRerenderOnTransaction: false,
    },
    []
  );

  return { editor };
};
